import { AIPromptLogSaver } from './AIPromptLogSaver';

import type { AIPromptLogEntry } from './AIPromptLogSaver';
import type { Client } from '@datocms/cma-client-browser';
import type ApiClient from '@src/apiClient/ApiClient';

export type AIPromptResponseMeta = {
  provider: string;
  model_id: string;
  /** DatoCMS ai_model record ID — resolved at complete time when not known at start */
  modelRecordId?: string;
  /** Time from request start to first content chunk (ms) */
  first_content_latency_ms?: number;
  tokens?: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
  };
};

type AIPromptLogStartEntry = Omit<
  AIPromptLogEntry,
  'result_status' | 'latency_ms' | 'error_message'
>;

/**
 * Lifecycle tracker for AI prompt calls — analogous to AIEventTracker
 * but persists structured records to DatoCMS (ai_prompt_log model).
 *
 * Usage:
 *   const promptLog = AIPromptLogger.start(datoClient, {
 *     prompt_title: 'Article',
 *     prompt_type: 'Blog',
 *     endpoint: 'regenerate-gpt-response-stream',
 *     modelRecordId: aiModel?.id,
 *     storyId: story.id,
 *   });
 *   try {
 *     // ... do work ...
 *     promptLog.complete();
 *   } catch (err) {
 *     promptLog.fail(err);
 *   }
 */
class AIPromptLoggerInstance {
  private startTime: number;
  private entry: AIPromptLogStartEntry;
  private client: Client | ApiClient;
  private ended = false;

  constructor(client: Client | ApiClient, entry: AIPromptLogStartEntry) {
    this.client = client;
    this.entry = entry;
    this.startTime = Date.now();
  }

  /**
   * PLAT-11207 — name the prompt after the run started.
   *
   * For a caller that resolved its prompt record up front the entry is complete
   * at `start`. A Smart Graphics control carries only a prompt id, and the only
   * thing that knows the title and type of that record is the endpoint's
   * response — so the identity arrives mid-run and is merged in here.
   */
  describePrompt(meta: {
    prompt_title?: string;
    prompt_type?: string;
    aiPromptId?: string;
  }): void {
    if (this.ended) return;
    this.entry = {
      ...this.entry,
      ...(meta.prompt_title && { prompt_title: meta.prompt_title }),
      ...(meta.prompt_type && { prompt_type: meta.prompt_type }),
      ...(meta.aiPromptId && { aiPromptId: meta.aiPromptId }),
    };
  }

  complete(meta?: AIPromptResponseMeta): void {
    if (this.ended) return;
    this.ended = true;

    const fullEntry: AIPromptLogEntry = {
      ...this.entry,
      modelRecordId: meta?.modelRecordId || this.entry.modelRecordId,
      ...(meta?.first_content_latency_ms != null && {
        first_content_latency_ms: Math.round(meta.first_content_latency_ms),
      }),
      latency_ms: Math.round(Date.now() - this.startTime),
      result_status: 'success',
      metadata: {
        ...this.entry.metadata,
        ...(meta?.provider && { provider: meta.provider }),
        ...(meta?.model_id && { model_id: meta.model_id }),
        ...(meta?.tokens && { tokens: meta.tokens }),
      },
    };

    AIPromptLogSaver.save(this.client, fullEntry);
  }

  fail(error: unknown, meta?: AIPromptResponseMeta): void {
    if (this.ended) return;
    this.ended = true;

    const fullEntry: AIPromptLogEntry = {
      ...this.entry,
      modelRecordId: meta?.modelRecordId || this.entry.modelRecordId,
      ...(meta?.first_content_latency_ms != null && {
        first_content_latency_ms: Math.round(meta.first_content_latency_ms),
      }),
      latency_ms: Math.round(Date.now() - this.startTime),
      result_status: 'error',
      error_message: error instanceof Error ? error.message : String(error),
      metadata: {
        ...this.entry.metadata,
        ...(meta?.provider && { provider: meta.provider }),
        ...(meta?.model_id && { model_id: meta.model_id }),
        ...(meta?.tokens && { tokens: meta.tokens }),
      },
    };

    AIPromptLogSaver.save(this.client, fullEntry);
  }
}

export const AIPromptLogger = {
  start(
    client: Client | ApiClient,
    entry: AIPromptLogStartEntry,
  ): AIPromptLoggerInstance {
    return new AIPromptLoggerInstance(client, entry);
  },
};
